import { useEffect, useState } from 'react';
import { snapshotRect } from '../../../utils/floatingMenuPosition';
import { CALLOUT_PALETTE, colorToRgb, parseHexColor } from '../utils/calloutTheme';

/**
 * Context menu (right-click / handle) plus IconPicker anchor for a callout.
 */
export const useCalloutMenu = ({ block, onUpdate }) => {
  // { anchorRect } | { x, y }, plus optional wrapIds from a multi-select open
  const [menuPos, setMenuPos] = useState(null);
  // Anchor rect for IconPicker — same beside-handle placement as BlockHandleMenu
  const [iconPickerAnchor, setIconPickerAnchor] = useState(null);
  const [hexDraft, setHexDraft] = useState('');

  const color = colorToRgb(block.props?.color) ? block.props.color : 'neutral';
  const isCustomColor = !CALLOUT_PALETTE[color];

  // Seed the hex field with the current custom color each time the menu opens
  useEffect(() => {
    if (menuPos) setHexDraft(isCustomColor ? color : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuPos]);

  // Right-clicking this callout's DRAG HANDLE (rendered by BlockRenderer)
  // also opens the menu — the handle broadcasts an event keyed by block id.
  useEffect(() => {
    const onHandleMenu = (e) => {
      if (e.detail?.blockId !== block.id) return;
      // Second click on the handle toggles the menu closed
      setMenuPos((prev) => {
        if (prev) return null;
        const wrapIds = Array.isArray(e.detail.wrapIds) ? e.detail.wrapIds : null;
        if (e.detail.anchorRect) {
          return { anchorRect: e.detail.anchorRect, wrapIds };
        }
        return { x: e.detail.x, y: e.detail.y, wrapIds };
      });
    };
    document.addEventListener('doc-callout-menu', onHandleMenu);
    return () => document.removeEventListener('doc-callout-menu', onHandleMenu);
  }, [block.id]);

  const pickColor = (e, nextColor) => {
    e.stopPropagation();
    onUpdate(block.id, { props: { ...block.props, color: nextColor } });
  };

  const pickSurface = (e, nextSurface) => {
    e.stopPropagation();
    onUpdate(block.id, { props: { ...block.props, surface: nextSurface } });
  };

  const applyHexDraft = () => {
    if (!parseHexColor(hexDraft)) return;
    const normalized = `#${hexDraft.trim().replace(/^#/, '').toLowerCase()}`;
    onUpdate(block.id, { props: { ...block.props, color: normalized } });
  };

  const openIconPicker = (e) => {
    e.stopPropagation();
    // Prefer the live drag handle; it may be unmounted once the cursor moved
    // onto the portaled menu, so fall back to the snapshot from menu open.
    const handle = document.querySelector(
      `[data-block-id="${block.id}"] > .block-drag-handle`
    );
    let anchorRect = null;
    if (handle) {
      anchorRect = snapshotRect(handle.getBoundingClientRect());
    } else if (menuPos?.anchorRect) {
      anchorRect = snapshotRect(menuPos.anchorRect);
    } else if (menuPos) {
      const x = menuPos.x || 0;
      const y = menuPos.y || 0;
      anchorRect = { left: x, top: y, right: x, bottom: y, width: 0, height: 0 };
    }
    setIconPickerAnchor(anchorRect);
    setMenuPos(null);
  };

  const handleIconChange = (newIcon) => {
    onUpdate(block.id, { props: { ...block.props, icon: newIcon || null } });
    // Clear / remove closes the picker so the empty callout is visible immediately
    if (!newIcon) setIconPickerAnchor(null);
  };

  return {
    menuPos,
    setMenuPos,
    iconPickerAnchor,
    setIconPickerAnchor,
    hexDraft,
    setHexDraft,
    pickColor,
    pickSurface,
    applyHexDraft,
    openIconPicker,
    handleIconChange,
  };
};
